import React, { memo, useCallback } from 'react';
import { getMessage } from '@tcc/i18n';
import { Checkbox } from '@tcc/crate';
import classNames from 'classnames';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setAllSelectedLayers, setSelectedLayerGroup } from 'manual';
import { Crop } from 'store/types/types';
import { getSelectedLayerGroup, getSelectedLayers, getYieldLayers } from './selectors';
import { getLayersGroupCheckBoxStates } from 'manual/util/zone-generation';
import { LAYERS_TYPES } from 'manual/constants';

interface CropItemProps {
  isSelected: boolean;
  crop: Crop;
  disabled: boolean;
  isIndeterminate: boolean;
  selectedLayerGroup: string;
  selectCrop: (cropId?: string) => void;
  selectAllLayers: (layerGroup: string, isSelected?: boolean) => void;
}

const CropItem = memo(function Item({
  isSelected,
  crop,
  disabled,
  isIndeterminate,
  selectedLayerGroup,
  selectCrop,
  selectAllLayers
}: CropItemProps) {
  const isSelectedCrop = selectedLayerGroup === crop['dfp-uuid'];
  const liClassString = classNames({
    isSelected: isSelectedCrop
  });

  const onClick = () => {
    selectCrop(crop['dfp-uuid']);
  };

  const onCheckboxChange = () => {
    selectAllLayers(crop['dfp-uuid'], isSelected);
  };

  return (
    <li
      key={crop['dfp-uuid']}
      className={liClassString}
      data-test-id={`crop-type-item-${crop.code}`}
      onClick={onClick}
    >
      <Checkbox
        checked={isSelected}
        onChange={onCheckboxChange}
        disabled={disabled}
        isIndeterminate={isIndeterminate}
        testId={`select-all-checkbox-${crop.code}`}
      />
      {getMessage(`crop--${crop.code}`)}
    </li>
  );
});

interface ZoneGenerationYieldCropSelecTypetionProps {
  crops: Crop[];
  selectedMapType: typeof LAYERS_TYPES[keyof typeof LAYERS_TYPES];
}

const ZoneGenerationYieldCropTypeSelection = ({
  crops,
  selectedMapType,
}: ZoneGenerationYieldCropSelecTypetionProps) => {
  const dispatch = useAppDispatch();
  const yieldLayers = useAppSelector(getYieldLayers);
  const selectedLayerGroup = useAppSelector(getSelectedLayerGroup);
  const selectedLayers = useAppSelector(getSelectedLayers);

  const selectCrop = useCallback((cropId: string) => {
    dispatch(setSelectedLayerGroup(cropId));
  }, [dispatch]);
  const selectAllLayers = useCallback((layerGroup: string, isSelected?: boolean) => {
    dispatch(setAllSelectedLayers(isSelected, 'crop', layerGroup));
  }, [dispatch]);

  return (
    <>
      <section data-test-id="layers-groups-header">
        {getMessage('label-text__generate-zones-layers-type-group-select-yield')}
      </section>
      <ul data-test-id="crop-type-group-list">
        {crops.map((crop) => {
          const layers = yieldLayers[crop['dfp-uuid']];
          const usableLayersByCropId = layers?.filter(({ isUsable }) => isUsable);
          const selectedLayersByCropId = selectedLayers?.filter(({ cropId }) => cropId === crop['dfp-uuid']);

          const {
            isChecked: isSelected,
            isDisabled,
            isIndeterminate
          } = getLayersGroupCheckBoxStates({
            groupUsableLayersCount: usableLayersByCropId?.length,
            selectedGroupLayersCount: selectedLayersByCropId?.length,
            allSelectedLayersCount: selectedLayers?.length,
            selectedMapType
          });

          return (
            <CropItem
              key={crop['dfp-uuid']}
              crop={crop}
              selectCrop={selectCrop}
              isSelected={isSelected}
              disabled={isDisabled}
              isIndeterminate={isIndeterminate}
              selectAllLayers={selectAllLayers}
              selectedLayerGroup={selectedLayerGroup}
            />
          );
        })}
      </ul>
    </>
  );
};

export default memo(ZoneGenerationYieldCropTypeSelection);
